import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LeftOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { CourseResourceApi } from './CourseResourceApi';
import type { CourseDetail, LessonItem } from './CourseResourceType';
import { CourseVideoPlayer } from './CourseVideoPlayer';
import { CoursePdfViewer } from './CoursePdfViewer';
import './CourseResourcePage.css';

type CourseResourceDetailPageProps = {
  spaceId: string;
  spaceProductId: string;
  courseId: string;
  onBack?: () => void;
};

/** 课程详情：课时目录 + 页内播放/阅读 */
export const CourseResourceDetailPage: React.FC<CourseResourceDetailPageProps> = ({
  spaceId,
  spaceProductId,
  courseId,
  onBack,
}) => {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<CourseDetail | null>(null);
  const [activeLessonId, setActiveLessonId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await CourseResourceApi.getCourseDetail({ spaceId, spaceProductId, courseId });
      setDetail(res);
      await CourseResourceApi.recordCourseOpen({ spaceId, spaceProductId, courseId });
      const first = res.lessons?.[0];
      setActiveLessonId(first?.id ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [courseId, spaceId, spaceProductId]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeLesson: LessonItem | null = useMemo(() => {
    if (!detail || !activeLessonId) return null;
    return detail.lessons.find((l) => l.id === activeLessonId) ?? null;
  }, [activeLessonId, detail]);

  const reportProgress = useCallback(
    async (lesson: LessonItem, progress: number) => {
      try {
        const completed = progress >= 100;
        await CourseResourceApi.reportLessonProgress({
          spaceId,
          spaceProductId,
          courseId,
          lessonId: lesson.id,
          progress,
          completed,
        });
        setDetail((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            lessons: prev.lessons.map((l) =>
              l.id === lesson.id ? { ...l, progress, completed } : l,
            ),
          };
        });
      } catch {
        /* 进度上报失败不阻断学习 */
      }
    },
    [courseId, spaceId, spaceProductId],
  );

  if (loading) {
    return (
      <div className="course-resource-loading">
        <Spin />
      </div>
    );
  }

  if (error || !detail) {
    return <div className="course-resource-empty">{error ?? '课程不存在'}</div>;
  }

  return (
    <div className="course-resource-detail-page">
      <div className="course-resource-detail-header">
        {onBack ? (
          <button type="button" className="course-resource-back" onClick={onBack}>
            <LeftOutlined className="course-resource-back-icon" aria-hidden />
            回到课程列表
          </button>
        ) : null}
        <div className="course-resource-detail-title">{detail.title}</div>
        {detail.intro ? <div className="course-resource-detail-intro">{detail.intro}</div> : null}
      </div>

      <div className="course-resource-detail-layout">
        <div className="course-resource-player-panel">
          {activeLesson ? (
            activeLesson.lessonType === 'pdf' ? (
              <CoursePdfViewer src={activeLesson.mediaUrl} />
            ) : (
              <CourseVideoPlayer
                src={activeLesson.mediaUrl}
                onProgress={(percent) => void reportProgress(activeLesson, percent)}
              />
            )
          ) : (
            <div className="course-resource-empty">请选择课时</div>
          )}
          {activeLesson ? <div className="course-resource-active-lesson-title">{activeLesson.title}</div> : null}
        </div>

        <div className="course-resource-lesson-list">
          <div className="course-resource-lesson-list-title">课时目录</div>
          {detail.lessons.map((lesson) => (
            <button
              key={lesson.id}
              type="button"
              className={`course-resource-lesson-item${lesson.id === activeLessonId ? ' course-resource-lesson-item--active' : ''}`}
              onClick={() => setActiveLessonId(lesson.id)}
            >
              <span className="course-resource-lesson-type">
                {lesson.lessonType === 'pdf' ? 'PDF' : '视频'}
              </span>
              <span className="course-resource-lesson-name">{lesson.title}</span>
              {lesson.completed ? <span className="course-resource-lesson-done">已学完</span> : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CourseResourceDetailPage;
